// Merkan · Glocke in der Kopfleiste mit Benachrichtigungen (EI-10)
// Zähler der ungelesenen Einträge, Liste als Seitenleiste (mobil bildschirmfüllend), gelesen per Antippen oder
// „Alle als gelesen“. Neue Einträge kommen per Echtzeit, zusätzlich wird jede Minute nachgezählt.
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Check, CheckCheck, Settings2, Trash2 } from 'lucide-react';
import { IcGlocke } from '../../ui/icons';
import { supabase } from '../../lib/supabase';
import { useSession } from '../../lib/session';
import { relativ, zeit } from '../../lib/format';
import { t, tn } from '../../lib/sprache';
import { fehlertext, Knopf, LadeZeilen, Leer, Panel, Segment, useMeldung } from '../../ui';
import { titelText, type Benachrichtigung } from './arten';
import './bn.css';

const SPALTEN = 'id, art, titel, text, link, daten, erstellt_am, gelesen_am';
// Lokaler Prüfstand ohne Realtime-Server: nur Nachzählen jede Minute, sonst meldet jede Seite einen WebSocket-Fehler
const MIT_ECHTZEIT = !/\/\/(127\.0\.0\.1|localhost)[:/]/.test(String(import.meta.env.VITE_SUPABASE_URL ?? ''));

export default function Glocke() {
  const { session, mandant } = useSession();
  const uid = session?.user.id ?? null;
  const mandantId = mandant?.mandant_id ?? null;
  const navigate = useNavigate();
  const melden = useMeldung();
  const [anzahl, setAnzahl] = useState(0);
  const [offen, setOffen] = useState(false);
  const [filter, setFilter] = useState<'ungelesen' | 'alle'>('ungelesen');
  const [liste, setListe] = useState<Benachrichtigung[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const offenRef = useRef(offen);
  offenRef.current = offen;

  const zaehlen = useCallback(async () => {
    if (!uid || !mandantId) return;
    const { count, error } = await supabase.from('benachrichtigungen').select('id', { count: 'exact', head: true })
      .eq('mandant_id', mandantId).eq('user_id', uid).is('gelesen_am', null);
    if (!error) setAnzahl(count ?? 0);
  }, [uid, mandantId]);

  const laden = useCallback(async () => {
    if (!uid || !mandantId) return;
    let q = supabase.from('benachrichtigungen').select(SPALTEN).eq('mandant_id', mandantId).eq('user_id', uid)
      .order('erstellt_am', { ascending: false }).limit(100);
    if (filter === 'ungelesen') q = q.is('gelesen_am', null);
    const { data, error } = await q;
    if (error) { setFehler(fehlertext(error)); return; }
    setFehler(null);
    setListe((data as Benachrichtigung[] | null) ?? []);
  }, [uid, mandantId, filter]);

  useEffect(() => {
    if (!uid || !mandantId) return;
    void zaehlen();
    const takt = window.setInterval(() => void zaehlen(), 60_000);
    const kanal = MIT_ECHTZEIT ? supabase.channel(`benachrichtigungen-${uid}-${mandantId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'benachrichtigungen', filter: `user_id=eq.${uid}` }, () => {
        void zaehlen();
        if (offenRef.current) void laden();
      })
      .subscribe() : null;
    const neu = () => { void zaehlen(); if (offenRef.current) void laden(); };
    window.addEventListener('merkan:benachrichtigungen', neu);
    return () => { window.clearInterval(takt); if (kanal) void supabase.removeChannel(kanal); window.removeEventListener('merkan:benachrichtigungen', neu); };
  }, [uid, mandantId, zaehlen, laden]);

  useEffect(() => { if (offen) void laden(); }, [offen, laden]);

  const gelesen = async (ids: string[] | null) => {
    if (!mandantId) return;
    const { error } = await supabase.rpc('benachrichtigungen_gelesen', { p_mandant: mandantId, p_ids: ids });
    if (error) { melden(fehlertext(error), true); return; }
    const jetzt = new Date().toISOString();
    setListe((l) => l && (filter === 'ungelesen' ? l.filter((b) => ids && !ids.includes(b.id)) : l.map((b) => (!ids || ids.includes(b.id) ? { ...b, gelesen_am: b.gelesen_am ?? jetzt } : b))));
    void zaehlen();
  };

  const entfernen = async (b: Benachrichtigung) => {
    const { error } = await supabase.from('benachrichtigungen').delete().eq('id', b.id);
    if (error) { melden(fehlertext(error), true); return; }
    setListe((l) => l && l.filter((x) => x.id !== b.id));
    void zaehlen();
  };

  const oeffnen = (b: Benachrichtigung) => {
    if (!b.gelesen_am) void gelesen([b.id]);
    setOffen(false);
    if (b.link) navigate(b.link);
  };

  if (!uid || !mandantId) return null;
  const titel = anzahl ? tn(anzahl, 'Benachrichtigungen, {n} ungelesen', 'Benachrichtigungen, {n} ungelesen') : t('Benachrichtigungen');

  return (
    <>
      <button type="button" className="bn-glocke" onClick={() => setOffen(true)} title={titel} aria-label={titel} data-tour="kopf-glocke" aria-haspopup="dialog">
        <IcGlocke />
        {anzahl > 0 && <span className="bn-zaehler" aria-hidden="true">{anzahl > 99 ? '99+' : anzahl}</span>}
      </button>
      {/* Kopfleiste hat backdrop-filter und wäre sonst Bezugsrahmen der festen Seitenleiste: daher in den body */}
      {offen && createPortal(<Panel
        offen={offen}
        titel={t('Benachrichtigungen')}
        untertitel={anzahl ? tn(anzahl, '{n} ungelesen', '{n} ungelesen') : t('Alles gelesen')}
        schliessen={() => setOffen(false)}
        fuss={(
          <>
            <Knopf art="leise" icon={<Settings2 />} onClick={() => { setOffen(false); navigate('/einstellungen/benachrichtigungen'); }} data-tour="glocke-einstellungen">{t('Einstellungen')}</Knopf>
            <Knopf icon={<CheckCheck />} onClick={() => void gelesen(null)} disabled={!anzahl} data-tour="glocke-alle-gelesen">{t('Alle als gelesen')}</Knopf>
          </>
        )}
      >
        <div className="bn-werkzeuge" data-tour="glocke-filter">
          <Segment<'ungelesen' | 'alle'> optionen={[{ wert: 'ungelesen', name: t('Ungelesen') }, { wert: 'alle', name: t('Alle') }]} wert={filter} setzen={setFilter} />
        </div>
        {fehler && <div className="klein fehler">{fehler}</div>}
        {!liste ? <LadeZeilen anzahl={4} /> : liste.length === 0 ? (
          <Leer icon={<IcGlocke />} titel={filter === 'ungelesen' ? t('Keine ungelesenen Benachrichtigungen') : t('Keine Benachrichtigungen')}
            text={t('Hier erscheinen Zuweisungen, Fälligkeiten, Freigaben, Erwähnungen und Antworten vom Support.')} />
        ) : (
          <ul className="bn-liste" aria-label={t('Benachrichtigungen')} data-tour="glocke-liste">
            {liste.map((b) => (
              <li key={b.id} className={`bn-eintrag${b.gelesen_am ? '' : ' bn-ungelesen'}`}>
                <button type="button" className="bn-haupt" onClick={() => oeffnen(b)}>
                  <span className="bn-punkt" aria-hidden="true" />
                  <span className="bn-inhalt">
                    <span className="bn-titel">{titelText(b)}</span>
                    {b.text && <span className="bn-text">{b.text}</span>}
                    <span className="bn-zeit" title={zeit(b.erstellt_am)}>{relativ(b.erstellt_am)}{b.gelesen_am ? '' : ` · ${t('ungelesen')}`}</span>
                  </span>
                </button>
                <span className="bn-aktionen">
                  {!b.gelesen_am && <Knopf art="leise" klein icon={<Check />} onClick={() => void gelesen([b.id])} title={t('Als gelesen markieren')} aria-label={t('Als gelesen markieren')} />}
                  <Knopf art="leise" klein icon={<Trash2 />} onClick={() => void entfernen(b)} title={t('Entfernen')} aria-label={t('Entfernen')} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>, document.body)}
    </>
  );
}
